import type { IconButtonProps } from '@chakra-ui/react'
import type { RefObject } from 'react'
import { useCallback } from 'react'
import { forwardRef, useState } from 'react'

import { toaster } from '@app/components/ui/toaster'
import AiFieldUpdateButton from '@app/pages/metrics/components/aiFieldUpdateButton'
import { useStore } from '@app/store'
import { StrategyNameSuggest } from '@graphql/documents/ai_assistant.graphql'
import type { StrategyNameSuggestMutation, StrategyNameSuggestMutationVariables } from '@graphql/queries'

type Props = Partial<IconButtonProps> & {
  strategyId: string
  onSuccess?: (result: string) => void
}

const AiPopulateStrategyNameButton = forwardRef<HTMLInputElement, Props>(
  ({ strategyId, onSuccess = () => {}, ...props }, ref) => {
    const [loading, setLoading] = useState(false)
    const actionMutation = useStore.use.actionMutation()

    const onClick = useCallback(async () => {
      setLoading(true)

      try {
        const { data, error } = await actionMutation<StrategyNameSuggestMutation, StrategyNameSuggestMutationVariables>(
          StrategyNameSuggest,
          {
            strategyId
          }
        )

        if (error?.graphQLErrors?.length) {
          toaster.create({
            title: 'Error',
            description: error.graphQLErrors[0].message,
            type: 'error'
          })

          return
        }

        // force this narrowing, as we know it's always an object (i.e. not a function)
        const forwardedRef = ref as RefObject<HTMLInputElement>
        const result = data?.strategyNameSuggest?.result

        if (forwardedRef?.current && result) {
          forwardedRef.current.value = result
        }

        onSuccess(result)
      } finally {
        setLoading(false)
      }
    }, [strategyId, actionMutation, ref, onSuccess])

    return <AiFieldUpdateButton loading={loading} onClick={onClick} data-cy="strategy-name-suggest" {...props} />
  }
)

export default AiPopulateStrategyNameButton
